/**
 * Opens the print-area positioner on a PIM product, from wherever a product is
 * at hand: its sheet, or an order that needs a proofsheet made from it.
 *
 * Everything the positioner needs is read off the product here — the areas
 * with their sizes and the supplier's print pictures, each option's pictures
 * in view order, the product's own pictures, the reseller's saved placements —
 * and what it saves is written back to the reseller sheet.
 */
define('pimPicturePositionerLauncher',[
  'jquery',
  'underscore',
  'bootbox',
  'pimUtil',
  'pimPicturePositionerView'
], function (
  $,
  _,
  bootbox,
  PimUtil,
  PimPicturePositionerView
) {
  'use strict';

  return {
    /**
     * @param model     a fetched PimProduct model with reseller data
     * @param startUrl  picture to open on, or null
     * @param onSaved   called once the placement is saved
     * @return true when the popup opened; false when there is no reseller sheet to save on
     */
    open: function (model, startUrl, onSaved) {
      var resProduct = model.getCurrentResProduct();
      if (!resProduct) {
        // Opened from an order, nobody has chosen a reseller sheet yet: the
        // product sheet's default is the first one, and here the one already
        // carrying placements comes before it, so what was placed is what
        // gets edited.
        var sheets = model.get('resellerData') ? model.get('resellerData').models : [];
        var placed = _.find(sheets, function (sheet) {
          return (sheet.get('pimPicturePositions') || []).length > 0;
        });
        if (placed || sheets.length) {
          model.setCurrentResProduct((placed || sheets[0]).get('id'));
          resProduct = model.getCurrentResProduct();
        }
      }
      if (!resProduct) {
        bootbox.alert(_.i18n('pimPositioner.noResellerSheet', null,
          'Create the reseller product first: the placement is saved on it.'));
        return false;
      }

      var data = model.get('data').toJSON();

      var areas = _.map(model.get('data').get('printAreas').models, function (pa) {
        var name = pa.get('name') && pa.get('name').get ? pa.get('name').get('values') : null;
        // The supplier's own picture of where this area prints, per colour,
        // when the print zones carry one: a guide for the hand placing it.
        var printPictures = [];
        var seen = {};
        _.each(pa.get('printZones') ? pa.get('printZones').toJSON() : [], function (zone) {
          _.each(zone.printPictures || [], function (picture) {
            if (picture.imageUrl && !seen[picture.imageUrl]) {
              seen[picture.imageUrl] = true;
              printPictures.push({optionCode: picture.optionColorCode, url: picture.imageUrl});
            }
          });
        });
        return {
          code: pa.get('code'),
          name: name ? PimUtil.getTranslation(name) : '',
          width: pa.get('width'),
          height: pa.get('height'),
          printPictures: printPictures
        };
      });

      // Each option's pictures once each, in view order: picture1..4 first,
      // then addImages — the same file under both is one picture, and the
      // portal's placement sits on the addImages entry.
      var options = [];
      _.each(data.productOptionCategories || [], function (category) {
        _.each(category.productOptions || [], function (option) {
          var byUrl = {};
          var pictures = [];
          _.each([option.picture1, option.picture2, option.picture3, option.picture4], function (url) {
            if (url && !byUrl[url]) {
              byUrl[url] = {url: url, supplierPositions: []};
              pictures.push(byUrl[url]);
            }
          });
          _.each(option.addImages || [], function (image) {
            if (!image.url) {
              return;
            }
            if (!byUrl[image.url]) {
              byUrl[image.url] = {url: image.url, supplierPositions: []};
              pictures.push(byUrl[image.url]);
            }
            if (image.productPicturePositions && image.productPicturePositions.length) {
              byUrl[image.url].supplierPositions = image.productPicturePositions;
            }
          });
          if (pictures.length) {
            options.push({
              pimId: option.pimId,
              code: option.code,
              label: (category.code || '') + ' · ' + (option.code || ''),
              pictures: pictures
            });
          }
        });
      });

      var productPictures = _.map(_.filter(data.productPictures || [], function (p) { return p.imageUrl; }),
        function (p) {
          return {url: p.imageUrl, supplierPositions: p.productPicturePositions || []};
        });

      var popup = new PimPicturePositionerView({
        areas: areas,
        options: options,
        productPictures: productPictures,
        saved: resProduct.get('pimPicturePositions') || [],
        start: startUrl ? {url: startUrl} : null
      });

      popup.on('save', function (entries, done) {
        resProduct.set('pimPicturePositions', entries);
        $.when(resProduct.savePicturePositions())
          .done(function () {
            done(false);
            if (onSaved) {
              onSaved(entries);
            }
          })
          .fail(function () { done(true); });
      });

      popup.show({
        title: _.i18n('pimPositioner.title', null, 'Print areas on the product pictures'),
        className: 'pim-picture-positioner-modal'
      });
      return true;
    }
  };
});

